/* eslint-disable @typescript-eslint/quotes */
import { COLORS, ERROR_TEXTS, REGEX, TYPOGRAPHY } from '@app/constants';
import styled from 'styled-components';
import { FC, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import isValid from 'date-fns/isValid';
import RadioTag from '@app/pageComponents/TherapistDetailsPage/RadioTag';
import User from '@app/ui/components/User';
import Dropdown from '@app/ui/components/Dropdown';
import Button from '@app/ui/components/Button';
import FlatBtn from '@app/ui/components/FlatBtn';
import StyledDatePicker from '@app/ui/components/StyledDatePicker';
import Radio from '@app/ui/components/Radio';
import { BreakpointsEnum, CustomError, TherapistData } from '@app/types';
import {
  useGetTherapistFreeAvailabilitiesQuery,
  useSendRequestSessionMutation,
} from '@app/store/api/sessions';
import { getDateInTimeRange, TimeMeasure } from '@app/utils/getDateInTimeRange';
import { AvailabilityResponseDto } from '@app/api';
import { getDateWithoutTime } from '@app/utils/getDateWithoutTime';
import { useClientSize } from '@app/hooks/';
import { useAppSelector } from '@app/store/hooks';
import { selectors } from '@app/store';
import { sendMetrik } from '@app/utils/sendMetrik';
import TextInput from '@app/ui/components/TextInput/TextInput';
import { getInputState } from '@app/utils/getInputState';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { toastService } from '@app/services/toastService';
import ConditionalWrapper from '@app/components/ConditionalWrapper';
import Tooltip from '@app/ui/components/Tooltip';
import OfferModal from '../OfferModal';
import { EnrollModalFormValues } from '@/app/pageComponents/TherapistDetailsPage/types';

export interface ModalFormValues {
  day: Date;
  remind: string;
  time?: string;
}

export interface ModalRequestFormValues {
  name: string;
  phone: string;
}

const options = [
  {
    value: '1',
    label: 'За час до начала',
  },
  {
    value: '8',
    label: 'За 8 часов до начала',
  },
  {
    value: '16',
    label: 'За 16 часов до начала',
  },
  {
    value: '24',
    label: 'За день до начала',
  },
  {
    value: '0',
    label: 'Не установлено',
  },
];

type EnrollModalProps = {
  defaultFormValues?: ModalFormValues;
  therapist: TherapistData;
  doesChange?: boolean;
  onCancel?: () => void;
  hasCard?: boolean;
  isOld?: boolean;
  onSubmitEnroll?: (availabilityId: string) => void;
  sessionStartDate?: Date;
};

const EnrollModal: FC<EnrollModalProps> = ({
  doesChange = false,
  hasCard = false,
  onCancel,
  isOld = false,
  onSubmitEnroll,
  therapist,
  sessionStartDate,
  ...props
}) => {

  const {

    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { dirtyFields },

  } = useForm<EnrollModalFormValues>();

  // // Предположим, что у нас есть какая-то логика для получения новых значений
  // useEffect(() => {

  // //   // TODO сделаь по аналогии, но брать значения из localStorage

  //   const newDay = new Date("2024-11-27T21:00:00.000Z");
  //   const newTime = "12:00";
  //   const newRemind = true;

  //   // Установка новых значений
  //   setValue("day", newDay);
  //   setValue("time", newTime);
  //   // setValue("remind", {value: '16', label: 'За 16 часов до начала'});

  // }, [ ]);

  const user = useAppSelector(selectors.auth.selectUser);
  const isMedsiUser = user?.utmCampaign === 'medsi';

  const [chosenDate, setChosenDate] = useState<Date | undefined>(
    getDateWithoutTime(new Date()),
  );

  const { data, isLoading } = useGetTherapistFreeAvailabilitiesQuery({
    therapistId: therapist.id,
  });

  const dates = useMemo(() => {
    return data?.availabilities.map(
      (availability) => new Date(availability.startDate),
    );
  }, [data]);

  const hours = useMemo(() => {
    if (!chosenDate || !data) {
      return [];
    }

    const filterAvailabilities = (
      availabilities: AvailabilityResponseDto[],
    ) => {
      return availabilities.filter(({ startDate }) => {
        const from = chosenDate;
        const to = getDateInTimeRange(from, 1, TimeMeasure.days);
        const availabilityDate = new Date(startDate);

        return availabilityDate >= from && availabilityDate < to;
      });
    };

    return filterAvailabilities(data.availabilities).map(
      ({ id, startDate }) => ({
        availabilityId: id,
        hour: `${new Date(startDate).getHours()}:00`,
      }),
    );
  }, [data, chosenDate]);

  const schema = yup
    .object({
      name: yup.string().required(ERROR_TEXTS.required),
      phone: yup
        .string()
        .required(ERROR_TEXTS.required)
        .matches(REGEX.anyPhone, { message: ERROR_TEXTS.phone }),
    })
    .required();

  const {
    handleSubmit: handleSubmitRequest,
    register: registerRequest,
    formState: { errors, touchedFields, isValid: isValidRequest },
  } = useForm<ModalRequestFormValues>({
    mode: 'onChange',
    resolver: yupResolver(schema),
    delayError: 500,
    defaultValues: {
      name: user.firstname,
      phone: user?.phone,
    },
  });

  const [sendRequestSession, { isLoading: isLoadingRequest }] =
    useSendRequestSessionMutation();

  const onSubmitRequest = handleSubmitRequest(async (data) => {
    try {
      await sendRequestSession({
        therapistId: therapist.id,
        name: data.name,
        phone: data.phone,
        patientUserId: user?.id,
        utmSource: user?.utmSource,
        utmMedium: user?.utmMedium,
        utmCampaign: user?.utmCampaign,
      }).unwrap();
      toastService.success({ title: 'Запрос отправлен' });
      onCancel && onCancel();
    } catch (err) {
      const error = err as CustomError;
      toastService.error({ title: 'Ошибка', content: error.data?.message || 'Ошибка при отправке запроса' });
    }
  });

  const onSubmit = (payload: ModalFormValues) => {
    const hour = hours.find((hour) => hour.hour === payload.time);
    sendMetrik('reachGoal', 'button_schedule');

    if (!hour) {
      return;
    }

    onSubmitEnroll && onSubmitEnroll(hour.availabilityId);
  };

  const remindDefaultValue = options.find(
    (item) => item.value === user.notificationBeforeTime,
  );

  const modalTitle = doesChange
    ? isOld
      ? 'Выберите дату и время для новой консультации'
      : 'Выберите новую дату для консультации'
    : 'Выберите дату и время для консультации';

  const isDateInvalid =
    sessionStartDate &&
    new Date() > getDateInTimeRange(sessionStartDate, -24, TimeMeasure.hours);

  const isSubmitButtonDisabled =
    isDateInvalid || !dirtyFields.time || hours.length === 0;

  const buttonLabel =
    doesChange && isSubmitButtonDisabled
      ? 'Выберите дату и время'
      : doesChange && !isOld
      ? 'Перенести запись'
      : 'Записаться';

  const { getIsBreakpoint } = useClientSize();
  const isMobile = getIsBreakpoint('sm');

  const isDatesEmpty = dates?.length === 0;

  const offerModal = OfferModal();

  if (isLoading) {
    return null;
  }

  return (
    <Root {...props}>
      {offerModal.Component}
      {isDatesEmpty ? (
        <>
          <ModalTitle>Записаться к специалисту</ModalTitle>
          <Subheading>
            Мы с Вами свяжемся и поможем организовать встречу
          </Subheading>
          <form onSubmit={onSubmitRequest}>
            <Container>
              <StyledTextInput
                label="Имя"
                {...registerRequest('name')}
                inputState={getInputState(touchedFields, errors, 'name')}
                textError={touchedFields.name ? errors.name?.message : ''}
                isError={Boolean(errors.name?.message) && touchedFields.name}
              />
              <StyledInput
                type="tel"
                inputMode="tel"
                label="Номер телефона"
                placeholder="Телефон"
                inputState={getInputState(touchedFields, errors, 'phone')}
                textError={touchedFields.phone ? errors.phone?.message : ''}
                isError={Boolean(errors.phone?.message) && touchedFields.phone}
                {...registerRequest('phone')}
              />
              <StyledButton
                styleType="primary"
                isLoading={isLoadingRequest}
                isLarge
                isDisabled={!isValidRequest}
              >
                Отправить запрос
              </StyledButton>
            </Container>
          </form>
          <ModalForm onSubmit={handleSubmit(onSubmit)}></ModalForm>
        </>
      ) : (
        <>
          <ModalTitle $hasCard={hasCard}>{modalTitle}</ModalTitle>
          {hasCard && (
            <StyledUser
              userName={`${therapist?.firstname}`}
              imgSrc={therapist?.avatarKey ?? undefined}
            />
          )}
          <ModalForm onSubmit={handleSubmit(onSubmit)}>
            <Controller
              control={control}
              name="day"
              render={({ field }) => {
                const fieldDate = new Date(field.value);
                const value = isValid(fieldDate) ? fieldDate : chosenDate;

                const handleDateChange = (date: Date) => {
                  const dateWithoutTime = getDateWithoutTime(date);

                  field.onChange(dateWithoutTime);
                  setChosenDate(dateWithoutTime);
                };

                return (
                  <CustomStyledDatePicker
                    isInline
                    onChange={handleDateChange}
                    selected={value}
                    minDate={new Date()}
                    futureSessions={dates}
                  />
                );
              }}
            />
            <Right>
              <AvailableTime>
                {hours.map((hour) => (
                  <li key={hour.availabilityId}>
                    <RadioTag
                      control={control}
                      value={hour.hour}
                      register={register}
                    />
                  </li>
                ))}
              </AvailableTime>
              {isMobile ? (
                <Controller
                  name="remind"
                  control={control}
                  render={({ field }) => {
                    return (
                      <>
                        <SendNotifications>
                          Отправлять напоминание
                        </SendNotifications>
                        <Notifications>
                          {options.map(({ label }, index) => (
                            <StyledRadio key={index} text={label} {...field} />
                          ))}
                        </Notifications>
                      </>
                    );
                  }}
                />
              ) : (
                <Controller
                  name="remind"
                  control={control}
                  render={({ field }) => (
                    <StyledDropdown
                      {...field}
                      options={options}
                      label="Напоминание"
                      placeholder="Не установлено"
                      defaultValue={remindDefaultValue}
                    />
                  )}
                />
              )}
              <ConditionalWrapper
                condition={isDateInvalid}
                wrapper={(children) => (
                  <Tooltip label="Перенести консультацию можно не позднее, чем за 24 часа до ее начала.">
                    {children}
                  </Tooltip>
                )}
              >
                <>
                  <StyledButton
                    isDisabled={isSubmitButtonDisabled}
                    styleType="primary"
                    isLarge
                  >
                    {buttonLabel}
                  </StyledButton>
                  {isMedsiUser && (
                    <OfferArgreeText>
                      Бронируя сессию вы соглашаетесь <br />с условиями{' '}
                      <OfferArgreeLink onClick={offerModal.open}>
                        Публичной оферты
                      </OfferArgreeLink>
                    </OfferArgreeText>
                  )}
                </>
              </ConditionalWrapper>

              {doesChange && onCancel && (
                <StyledFlatBtn
                  type="button"
                  styleType="cancel"
                  onClick={() => onCancel()}
                >
                  Отменить текущую запись
                </StyledFlatBtn>
              )}
            </Right>
          </ModalForm>
          <TimezoneNotify>
            Время соответсвует часовому поясу на вашем устройстве
          </TimezoneNotify>
        </>
      )}
    </Root>
  );
};

export default EnrollModal;

const Root = styled.div`
  padding: 40px 0 0 0;

  @media (max-width: ${BreakpointsEnum.sm}px) {
    padding: 36px 0 0 0;
  }
`;

interface StyleProps {
  $hasCard?: boolean;
}

const ModalTitle = styled.h3<StyleProps>`
  ${TYPOGRAPHY.heading3Bold24}
  color: ${COLORS.grayscale700};
  margin-bottom: ${({ $hasCard }) => ($hasCard ? '16px' : '42px')};

  @media (max-width: ${BreakpointsEnum.sm}px) {
    ${TYPOGRAPHY.headline1Semibold18}
    margin-bottom: ${({ $hasCard }) => ($hasCard ? '16px' : '24px')};
  }
  @media (max-width: ${BreakpointsEnum.s}px) {
    max-width: 260px;
  }
`;

const StyledUser = styled(User)`
  margin-bottom: 24px;
`;

const ModalForm = styled.form`
  display: flex;

  @media (max-width: ${BreakpointsEnum.sm}px) {
    flex-direction: column;
  }
`;

const Right = styled.div`
  margin-left: 40px;
  width: min(100%, 284px);

  @media (max-width: ${BreakpointsEnum.sm}px) {
    margin-left: 0;
    margin-top: 13px;
    border-top: 1px solid ${COLORS.grayscale100};
    padding-top: 12px;
  }
`;

const StyledDropdown = styled(Dropdown)`
  margin-bottom: 12px;

  & > div {
    max-width: unset;
  }

  @media (max-width: ${BreakpointsEnum.sm}px) {
    margin-bottom: 12px;
  }
`;

const StyledButton = styled(Button)`
  margin-bottom: 8px;
  width: 100%;

  background-color: ${COLORS.secondary300};
  color: ${COLORS.black};

  &:hover {
    background-color: ${COLORS.secondary400} !important;
    color: ${COLORS.black} !important;
  }
`;

const StyledFlatBtn = styled(FlatBtn)`
  width: 100%;
  padding-top: 12px;
  padding-bottom: 12px;
`;

const AvailableTime = styled.ol`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fill, 65px);
  row-gap: 16px;
  column-gap: 8px;
  margin-bottom: 24px;

  li label div {
    width: 58px;
    height: 36px;
    display: flex;
    justify-content: center;

    font-size: 14px;
    font-weight: 500;
    line-height: 20px;

    padding-left: 12px;
    padding-right: 12px;
    border-radius: 20px;
  }
`;

const CustomStyledDatePicker = styled(StyledDatePicker)`
  min-height: min-content;

  @media (max-width: ${BreakpointsEnum.sm}px) {
    width: 100%;
    display: flex;
    justify-content: center;
  }
`;

const Notifications = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
`;

const StyledRadio = styled(Radio)`
  margin-bottom: 12px;
  align-items: flex-start;
  div {
    min-width: 16px;
    min-height: 16px;
    margin-top: 2px;
  }
  span {
    margin-left: 10px;
    ${TYPOGRAPHY.footnoteRegular13};
    color: ${COLORS.grayscale600};
  }
  &:last-of-type {
    margin-bottom: 0;
  }
`;

const SendNotifications = styled.div`
  ${TYPOGRAPHY.headline2Medium16}
  margin-bottom: 12px;
`;

const TimezoneNotify = styled.p`
  ${TYPOGRAPHY.body1Regular13}
  text-align: center;
  padding-top: 8px;

  @media (max-width: ${BreakpointsEnum.sm}px) {
    padding-bottom: 28px;
  }
`;

const Subheading = styled.p`
  ${TYPOGRAPHY.body1Regular16}
`;

export const StyledInput = styled(TextInput)`
  width: 100%;
  &:not(:last-child) {
    margin-bottom: 32px;
  }
`;

const StyledTextInput = styled(TextInput)`
  width: 100%;
  max-width: 400px;
  margin-bottom: 24px;

  &:last-of-type {
    margin-bottom: 0;
  }

  input:read-only {
    cursor: not-allowed;
  }

  @media (max-width: ${BreakpointsEnum.sm}px) {
    max-width: 100%;
    margin-bottom: 8px;
  }
`;

const Container = styled.div`
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  margin-top: 20px;
`;

const OfferArgreeText = styled.p`
  font-size: 12px;
  text-align: center;
  margin: 2px 0 6px;
  opacity: 0.65;
`;

const OfferArgreeLink = styled.button`
  color: #055951;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
`;
